import { cx } from "@unizpay/ui";
import { AlertTriangle, Check, Clock, X } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";

const REDIRECT_SECONDS = 5;

const icons = {
  ok: Check,
  warn: AlertTriangle,
  danger: X,
  neutral: Clock,
};

/** Final (or waiting) state of the checkout: an icon, a title and an explanation, centered. */
export function Outcome({
  tone,
  title,
  children,
}: {
  tone: "ok" | "warn" | "danger" | "neutral";
  title: string;
  children?: ReactNode;
}) {
  const Icon = icons[tone];
  return (
    <div className="pt-2 text-center">
      <span
        className={cx(
          "mx-auto flex size-14 items-center justify-center rounded-full ring-8",
          tone === "ok" && "bg-ok text-white ring-ok-subtle",
          tone === "warn" && "bg-warn-subtle text-warn ring-warn-subtle/50",
          tone === "danger" && "bg-danger-subtle text-danger ring-danger-subtle/50",
          tone === "neutral" && "bg-subtle text-fg-muted ring-subtle/50",
        )}
      >
        <Icon aria-hidden="true" className="size-6" strokeWidth={tone === "ok" ? 2.5 : 2} />
      </span>
      <h1 className="mt-6 text-xl font-semibold tracking-tight">{title}</h1>
      {children && (
        <div className="mx-auto mt-2 max-w-sm space-y-3 text-sm leading-relaxed text-fg-muted">
          {children}
        </div>
      )}
    </div>
  );
}

export function SuccessRedirect({ url }: { url: string }) {
  const [left, setLeft] = useState(REDIRECT_SECONDS);
  useEffect(() => {
    if (left <= 0) {
      window.location.assign(url);
      return;
    }
    const t = window.setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [left, url]);
  return (
    <p>
      Returning you to the merchant in {left}s.{" "}
      <a href={url} className="font-medium text-fg underline underline-offset-2 hover:text-accent">
        Return now
      </a>
    </p>
  );
}
